import { Box, Flex, Icon, SimpleGrid, Text } from "@chakra-ui/react";
import NextLink from "next/link";
import { FC, ReactNode } from "react";

const iconData: Record<string, string[]> = {
  audiences: [
    "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2",
    "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z",
    "M22 21v-2a4 4 0 0 0-3-3.87",
    "M16 3.13a4 4 0 0 1 0 7.75",
  ],
  lifecycle: [
    "M3 17h4v4H3z",
    "M10 13h4v8h-4z",
    "M17 7h4v14h-4z",
    "M5 13l5-4",
    "M12 9l5-4",
  ],
  sync: [
    "M5 12H3l4-4 4 4H9a3 3 0 0 0 6 0",
    "M19 12h2l-4 4-4-4h2a3 3 0 0 0-6 0",
    "M7 8a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3",
    "M7 16a3 3 0 0 0 3 3h4a3 3 0 0 0 3-3",
  ],
  realtime: [
    "M13 2 3 14h9l-1 8 10-12h-9l1-8Z",
  ],
  identity: [
    "M8 7a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z",
    "M16 7a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z",
    "M12 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z",
    "M8 7v2a4 4 0 0 0 4 4",
    "M16 7v2a4 4 0 0 1-4 4",
    "M12 15v-2",
  ],
  ai: [
    "M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.582a.5.5 0 0 1 0 .962L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0L9.937 15.5Z",
    "M20 3v4",
    "M22 5h-4",
  ],
  governance: [
    "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z",
    "M9 12l2 2 4-4",
  ],
  matchbooster: [
    "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2",
    "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z",
    "M19 8l3-3",
    "M22 5h-4",
    "M22 5v4",
  ],
};

const CardIcon: FC<{ name: string; size: number; color: string }> = ({
  name,
  size,
  color,
}) => {
  const paths = iconData[name];
  if (!paths) return null;

  return (
    <Icon
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      color={color}
      boxSize={`${size}px`}
    >
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </Icon>
  );
};

type UseCaseCardProps = {
  title: string;
  href: string;
  variant?: "primary" | "secondary";
  icon?: string;
  destination?: string;
  children: ReactNode;
};

export const UseCaseCard: FC<Readonly<UseCaseCardProps>> = ({
  title,
  href,
  variant = "primary",
  icon,
  destination,
  children,
}) => {
  const isPrimary = variant === "primary";

  return (
    <NextLink passHref href={href}>
      <Flex
        as="a"
        direction="column"
        border="1px solid"
        borderColor={isPrimary ? "primary.border" : "base.border"}
        borderRadius="xl"
        padding={isPrimary ? 6 : 5}
        height="100%"
        bg={isPrimary ? "primary.background" : "base.lightBackground"}
        boxShadow="none"
        textDecoration="none"
        cursor="pointer"
        outline="none"
        _hover={{
          textDecoration: "none",
          bg: isPrimary ? "primary.backgroundHover" : "base.background",
        }}
        _focusVisible={{ boxShadow: "outline" }}
        transition="background-color 150ms ease-out"
      >
        {icon && (
          <Box mb={isPrimary ? 3 : 2}>
            <CardIcon
              name={icon}
              size={isPrimary ? 24 : 20}
              color={isPrimary ? "primary.base" : "text.tertiary"}
            />
          </Box>
        )}
        <Text
          fontFamily="heading"
          fontWeight="semibold"
          fontSize={isPrimary ? "lg" : "md"}
          color="text.primary"
          mb={isPrimary ? 2 : 1}
        >
          {title}
        </Text>
        <Text
          fontSize={isPrimary ? "md" : "sm"}
          color="text.secondary"
          lineHeight="1.6"
          flex="1"
          mb={destination ? (isPrimary ? 4 : 3) : 0}
        >
          {children}
        </Text>
        {destination && (
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color="primary.base"
            mt="auto"
            display="flex"
            alignItems="center"
            gap={1}
            transition="color 150ms ease-out"
            sx={{ "a:hover &": { color: "primary.hover" } }}
          >
            {destination}
            <Box as="span" fontSize="sm" transition="transform 150ms ease-out"
              sx={{ "a:hover &": { transform: "translateX(4px)" } }}
            >
              →
            </Box>
          </Text>
        )}
      </Flex>
    </NextLink>
  );
};

type UseCaseGridProps = {
  columns?: number;
  children: ReactNode;
};

export const UseCaseGrid: FC<Readonly<UseCaseGridProps>> = ({
  columns,
  children,
}) => {
  const cols = columns ?? 3;
  return (
    <SimpleGrid
      columns={{ base: 1, md: Math.min(cols, 2), lg: cols }}
      spacing={5}
      my={6}
    >
      {children}
    </SimpleGrid>
  );
};
